import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiX } from 'react-icons/fi';
import { billingRequest, billingError, getBillingSession, notifyBillingUpdated } from './billingClient';
import { ChargeLink } from './BillingBalance';
import useBillingWallet from './useBillingWallet';
import './AiWallet.scss';

const credits = (value) => (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && Number.isSafeInteger(Number(value)) ? `${Number(value).toLocaleString('ko-KR')} C` : '—';
const stateLabels = { reserved: '처리 중', completed: '완료', released: '차감 취소', failed: '실패' };
const featureLabels = { teacher_omr_extract: '모범답안 판독', teacher_omr_sheet_extract: '전체 모범답안 판독' };
const activityLabels = { grant: '충전', manual_grant: '관리자 충전', manual_topup: '관리자 충전', reserve: 'AI 사용 예약', capture: 'AI 사용', release: '예약 해제', refund: '환불', payment: '결제 충전', charge: '충전', adjust: '관리자 조정' };

export default function AiWallet() {
  const navigate = useNavigate();
  const { data, loading, error, requiresLogin, balance, refresh } = useBillingWallet();
  const [action, setAction] = useState({ token: '', busy: false, error: '', job: null });
  const mounted = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const token = getBillingSession()?.token || '';
  const activeAction = action.token === token && !requiresLogin ? action : { busy: false, error: '', job: null };

  async function checkJob(requestId) {
    const requestToken = getBillingSession()?.token;
    const isCurrent = () => mounted.current && requestToken && getBillingSession()?.token === requestToken && Number(getBillingSession()?.expiresAt) > Date.now();
    setAction({ token: requestToken, busy: true, error: '', job: null });
    try {
      const result = await billingRequest('job', { requestId, includeResult: false });
      if (!isCurrent()) return;
      if (result.reconciliationRequired) throw new Error('사용량 확인이 필요합니다. 관리자에게 문의해 주세요.');
      setAction({ token: requestToken, busy: false, error: '', job: { status: result.status, chargedCredits: result.chargedCredits } });
      notifyBillingUpdated();
    } catch (requestError) {
      if (isCurrent()) setAction({ token: requestToken, busy: false, error: billingError(requestError), job: null });
    }
  }

  const wallet = data?.wallet || data || {};
  const entries = data?.ledger || data?.transactions || data?.history || [];
  const jobs = data?.jobs || [];
  const ready = data && data.enabled !== false && !requiresLogin && balance !== null;
  const busy = loading || activeAction.busy;

  return <section className="ai-wallet">
    <header className="ai-wallet-header"><div><small>AI K-EDU</small><h1>내 AI 지갑</h1></div><button type="button" aria-label="홈으로 닫기" onClick={() => navigate('/homeApp')}><FiX aria-hidden="true" /></button></header>
    <p className="ai-wallet-intro">선생님 본인이 실행한 AI 작업에 본인의 크레딧을 사용합니다.</p>
    <button className="ai-wallet-refresh" type="button" disabled={busy} onClick={refresh}><FiRefreshCw aria-hidden="true" />{loading ? '잔액 확인 중…' : '잔액 새로고침'}</button>
    {requiresLogin && <p className="ai-wallet-error" role="alert">본인의 잔액을 확인하려면 다시 로그인해 주세요. <Link to="/login">다시 로그인</Link></p>}
    {error && !requiresLogin && <p className="ai-wallet-error" role="alert">{error} 새로고침으로 다시 확인해 주세요.</p>}
    {data?.enabled === false && <p className="ai-wallet-notice" role="status">크레딧 서비스를 준비 중입니다. 잠시 후 다시 확인해 주세요.</p>}

    {ready && <section className="ai-wallet-balance" aria-label="내 크레딧 잔액" aria-busy={loading}>
      <span>사용 가능한 크레딧</span><strong>{loading ? '확인 중…' : credits(balance)}</strong>
      <p>유료 {credits(wallet.paidCredits ?? wallet.paid)} · 무료 {credits(wallet.freeCredits ?? wallet.free)} · 사용 예약 {credits(wallet.reservedCredits ?? wallet.reserved)}</p>
    </section>}

    <section className="ai-wallet-card ai-wallet-charge">
      <h2>크레딧 충전</h2>
      <p>홈페이지의 ‘충전하기’ 메뉴에서 이용권을 선택해 주세요.</p>
      <ChargeLink />
      <small>현재 서비스와 같은 계정으로 로그인해 주세요. 충전 후 돌아오면 잔액이 갱신됩니다.</small>
    </section>

    {ready && <>
      <section className="ai-wallet-card">
        <h2>사용·충전 내역</h2>
        <p className="ai-wallet-help">AI 작업을 시작하면 예상 금액을 예약하고, 완료 후 실제 이용 요금을 확정합니다.</p>
        {entries.length ? <div className="ai-wallet-history"><table><thead><tr><th>일시</th><th>내용</th><th>사용 가능 잔액 증감</th><th>실제 사용</th></tr></thead><tbody>
          {entries.map((item, index) => <tr key={item.id || item.transactionId || index}>
            <td data-label="일시">{item.createdAt ? new Date(item.createdAt).toLocaleString('ko-KR') : '—'}</td>
            <td data-label="내용">{activityLabels[item.type || item.kind] || item.featureCode || item.type || item.kind || '—'}{(item.reason || item.description) && <small>{item.reason || item.description}</small>}</td>
            <td data-label="잔액 증감">{credits(item.delta ?? (Number(item.paidDelta || 0) + Number(item.freeDelta || 0)))}</td>
            <td data-label="실제 사용">{item.type === 'capture' ? credits(item.chargedCredits ?? 0) : '—'}</td>
          </tr>)}
        </tbody></table></div> : <p className="ai-wallet-empty">아직 사용·충전 내역이 없습니다.</p>}
      </section>

      <section className="ai-wallet-card">
        <h2>AI 작업 확인</h2>
        <p className="ai-wallet-help">작업 상태와 차감된 크레딧을 확인하세요.</p>
        {activeAction.error && <p className="ai-wallet-error" role="alert">{activeAction.error}</p>}
        {activeAction.busy && <p role="status">작업 상태를 확인하고 있습니다…</p>}
        {activeAction.job && <p className="ai-wallet-notice" role="status">작업 상태: {stateLabels[activeAction.job.status] || activeAction.job.status} · 차감 {credits(activeAction.job.chargedCredits ?? 0)}</p>}
        {jobs.length ? <div className="ai-wallet-history"><table><thead><tr><th>기능·모델</th><th>차감</th><th>상태</th></tr></thead><tbody>
          {jobs.map((job) => <tr key={job.jobId || job.requestId}>
            <td data-label="기능·모델">{featureLabels[job.featureCode] || job.featureCode}<small>{job.modelCode}</small></td>
            <td data-label="차감">{credits(job.chargedCredits ?? 0)}</td>
            <td data-label="상태">{stateLabels[job.status] || job.status}{job.status === 'reserved' && <button type="button" disabled={busy} onClick={() => checkJob(job.requestId)}>처리 상태 확인</button>}</td>
          </tr>)}
        </tbody></table></div> : <p className="ai-wallet-empty">아직 AI 작업 내역이 없습니다.</p>}
      </section>
    </>}
  </section>;
}
